import { GraphQLResponse, RequestParameters, Variables } from 'relay-runtime';
import { Auth0ContextInterface } from '@auth0/auth0-react';
import { createAssociativeAuthJWT } from '@resideo/savannahql-jwt';

import auth0Config, {
  USE_PERSISTED_QUERIES,
  PERSISTED_QUERIES_APP_NAME,
  PERSISTED_QUERIES_APP_VERSION,
  ENABLE_MOCKS,
  MOCK_AUTH,
  MOCK_USERID,
  MOCK_PORTAL_ADMIN,
  MOCK_TRUSTED_NODES,
  GRAPHQL_MOCK_SCENARIO,
  APOLLO_CLIENT_NAME,
} from 'config';
import * as Sentry from '@sentry/browser';

interface FetchQueryFactory {
  getAccessToken: Auth0ContextInterface['getAccessTokenSilently'];
  url: string;
}

export const encodeId = ({ appName, appVersion, md5Hash }) =>
  btoa(`${appName}:${appVersion}:${md5Hash}`);

const formatUrl = (url: string, operation: RequestParameters) =>
  USE_PERSISTED_QUERIES
    ? `${url}/${encodeId({
        appName: PERSISTED_QUERIES_APP_NAME,
        appVersion: PERSISTED_QUERIES_APP_VERSION,
        md5Hash: operation.id,
      })}`
    : url;

export function fetchQueryFactory({ getAccessToken, url }: FetchQueryFactory) {
  return async function fetchQuery(
    operation: RequestParameters,
    variables: Variables
  ): Promise<GraphQLResponse> {
    const accessToken = await getAccessToken();
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'apollographql-client-name': APOLLO_CLIENT_NAME,
      'apollographql-client-version': PERSISTED_QUERIES_APP_VERSION,
      Authorization: `Bearer ${accessToken}`,
    };

    if (MOCK_AUTH && !ENABLE_MOCKS) {
      headers['x-scenario-name'] = GRAPHQL_MOCK_SCENARIO;

      headers.Authorization = `Bearer ${createAssociativeAuthJWT({
        isResideoCustomerServiceUser: MOCK_PORTAL_ADMIN,
        authNamespace: auth0Config.namespace,
        identityNode: { id: MOCK_USERID },
        trustedNodes: JSON.parse(MOCK_TRUSTED_NODES),
      })}`;

      // check to see if user should be treated as a portal admin
      if (MOCK_PORTAL_ADMIN) {
        headers['x-force-user-role'] = 'PORTAL_ADMIN';
      }
    }

    const response = await fetch(formatUrl(url, operation), {
      method: 'POST',
      headers,
      body: JSON.stringify({
        ...(!USE_PERSISTED_QUERIES && { query: operation.text }),
        variables,
      }),
    });

    if (!response.ok) {
      let message = '';
      switch (response.status) {
        case 400:
          message = 'Bad Request';
          break;
        case 401:
          message = 'Unauthorized status from GraphQL';
          break;
        case 403:
          message = 'Unauthorized request';
          break;
        case 404:
          message = 'Wrong resource URL';
          break;
        case 500:
          message = 'Internal Server Error';
          break;
        default:
          message = 'Unknown status ' + response.status;
      }
      Sentry.captureException(message + ' - URL:' + url);
      throw new Error(message);
    }

    const result = await response.json();

    return result;
  };
}
